import { createFileRoute } from "@tanstack/react-router";

import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";
import { Reveal } from "@/components/Reveal";
import { Aurora } from "@/components/Aurora";
import { CtaButton } from "@/components/CtaButton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/styleguide")({
  head: () => ({
    meta: [
      { title: "Style Guide | LAA Consulting" },
      {
        name: "description",
        content:
          "Internal design system reference for LAA Concierge Consulting: color tokens, type scale, components.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Style Guide | LAA Consulting" },
      {
        property: "og:description",
        content: "Internal design system reference for LAA Concierge Consulting.",
      },
    ],
  }),
  component: StyleGuide,
});

const colorScales: Array<{ name: string; tokens: string[] }> = [
  {
    name: "ink (neutral)",
    tokens: [
      "ink-950",
      "ink-900",
      "ink-800",
      "ink-700",
      "ink-600",
      "ink-500",
      "ink-400",
      "ink-300",
      "ink-200",
      "ink-100",
      "ink-50",
    ],
  },
  { name: "paper (canvas)", tokens: ["paper-0", "paper-50", "paper-100"] },
  {
    name: "brand (teal — primary)",
    tokens: [
      "brand-900",
      "brand-800",
      "brand-700",
      "brand-650",
      "brand-600",
      "brand-500",
      "brand-400",
      "brand-300",
      "brand-200",
      "brand-100",
      "brand-50",
    ],
  },
  {
    name: "accent (violet — secondary, decorative only)",
    tokens: ["accent-700", "accent-600", "accent-500", "accent-400"],
  },
];

const semanticRoles: Array<{ label: string; token: string }> = [
  { label: "Primary", token: "brand-650" },
  { label: "Accent", token: "accent-600" },
  { label: "Ring / focus", token: "brand-700" },
  { label: "Background", token: "paper-50" },
  { label: "Danger", token: "danger" },
  { label: "Success", token: "success" },
  { label: "Warning", token: "warning" },
];

const typeScale: Array<{ label: string; className: string; sample: string }> = [
  { label: "display-xl", className: "font-display text-display-xl text-ink-900", sample: "Display XL" },
  { label: "display-lg", className: "font-display text-display-lg text-ink-900", sample: "Display LG" },
  { label: "h2", className: "font-display text-h2 text-ink-900", sample: "Heading 2" },
  { label: "h3", className: "font-display text-h3 text-ink-900", sample: "Heading 3" },
  { label: "body-lg", className: "text-body-lg text-ink-600", sample: "Body large — used for lead paragraphs." },
  { label: "body", className: "text-body text-ink-600", sample: "Body — the default paragraph size." },
  { label: "body-sm", className: "text-body-sm text-ink-500", sample: "Body small — captions and helper text." },
  {
    label: "eyebrow",
    className: "font-mono text-eyebrow uppercase tracking-[0.16em] text-brand-700",
    sample: "Eyebrow label",
  },
  { label: "stat", className: "font-display text-stat text-ink-900", sample: "248" },
];

function Swatch({ token }: { token: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="h-16 w-full rounded-md border border-ink-200"
        style={{ backgroundColor: `var(--${token})` }}
      />
      <span className="font-mono text-eyebrow uppercase text-ink-500">{token}</span>
    </div>
  );
}

function ButtonTiers() {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-body-sm font-medium text-ink-900">Loud — CtaButton (one per page view)</p>
        <div className="mt-3">
          <CtaButton>Book a Free AI Call</CtaButton>
        </div>
      </div>
      <div>
        <p className="text-body-sm font-medium text-ink-900">Medium — Button variant=&quot;secondary&quot;</p>
        <div className="mt-3">
          <Button variant="secondary">See services</Button>
        </div>
      </div>
      <div>
        <p className="text-body-sm font-medium text-ink-900">Quiet — Button variant=&quot;ghost&quot;</p>
        <div className="mt-3">
          <Button variant="ghost">Read the approach</Button>
        </div>
      </div>
      <div>
        <p className="text-body-sm font-medium text-ink-900">
          Loud on dark canvas — CtaButton tone=&quot;dark&quot;
        </p>
        <div className="mt-3 rounded-2xl bg-ink-900 p-8">
          <CtaButton tone="dark">Book a Free AI Call</CtaButton>
        </div>
      </div>
    </div>
  );
}

const motionUtilities = [
  { name: "laa-rise-1 … laa-rise-6", note: "Staggered hero entrance steps, CSS keyframe based." },
  { name: "laa-ringpath", note: "Animates an SVG ring path stroke for progress/loader motifs." },
  { name: "laa-bar", note: "Grows a bar chart column into place." },
  { name: "laa-col", note: "Grows a vertical column/stat into place." },
  { name: "cta-glow", note: "Soft glow treatment behind the primary CtaButton on light canvases." },
  { name: "cta-sheen", note: "Sheen sweep overlay on CtaButton hover." },
  { name: "Reveal", note: "Scroll-triggered reveal component (see live demo below)." },
] as const;

function StyleGuide() {
  return (
    <main>
      <Section>
        <Container>
          <Eyebrow>Design system</Eyebrow>
          <h1 className="mt-4 font-display text-display-xl text-ink-900">Style guide</h1>
          <p className="mt-6 max-w-[68ch] text-body-lg text-ink-600">
            An honest reference to the tokens, type scale, motion utilities, and components
            currently in use across laaconsulting.com. Nothing shown here is legacy or planned —
            if it's on this page, it's live.
          </p>

          <div className="mt-16 space-y-12">
            {colorScales.map((scale) => (
              <div key={scale.name}>
                <Eyebrow>{scale.name}</Eyebrow>
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
                  {scale.tokens.map((t) => (
                    <Swatch key={t} token={t} />
                  ))}
                </div>
              </div>
            ))}

            <div>
              <Eyebrow>Semantic roles</Eyebrow>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
                {semanticRoles.map((role) => (
                  <div key={role.label} className="flex flex-col gap-2">
                    <div
                      className="h-16 w-full rounded-md border border-ink-200"
                      style={{ backgroundColor: `var(--${role.token})` }}
                    />
                    <span className="text-body-sm text-ink-900">{role.label}</span>
                    <span className="font-mono text-eyebrow uppercase text-ink-500">
                      {role.token}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <Section tone="raised">
        <Container>
          <Eyebrow>Typography scale</Eyebrow>
          <div className="mt-8 space-y-8">
            {typeScale.map((step) => (
              <div key={step.label}>
                <p className="font-mono text-eyebrow uppercase text-ink-500">{step.label}</p>
                <p className={`mt-2 max-w-[60ch] ${step.className}`}>{step.sample}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <Eyebrow>Buttons — three tiers</Eyebrow>
          <div className="mt-8">
            <ButtonTiers />
          </div>
        </Container>
      </Section>

      <Section tone="raised">
        <Container>
          <Eyebrow>Cards / surfaces</Eyebrow>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Card className="rounded-2xl border border-ink-200 bg-paper-0 p-6 shadow-[0_1px_2px_oklch(0.1831_0.0309_263.38/0.05)] transition-brand hover:border-brand-600/40 md:p-8">
              <CardHeader className="p-0">
                <CardTitle>Concierge advisory</CardTitle>
              </CardHeader>
              <CardContent className="p-0 pt-4">
                <p className="text-body text-ink-600">
                  Light card surface. White on paper, hover border brand-600/40.
                </p>
              </CardContent>
            </Card>
            <Card className="border-paper-0/12 bg-paper-0/[0.04] p-6 text-paper-0 md:p-8 dark bg-ink-900">
              <CardHeader className="p-0">
                <CardTitle className="text-paper-0">Dark card</CardTitle>
              </CardHeader>
              <CardContent className="p-0 pt-4">
                <p className="text-body text-ondark-body">
                  Dark card surface, used only inside `Section tone=&quot;dark&quot;`.
                </p>
              </CardContent>
            </Card>
          </div>
        </Container>
      </Section>

      <Section>
        <Container variant="narrow">
          <Eyebrow>Motion utilities</Eyebrow>
          <p className="mt-4 text-body-sm text-ink-600">
            Every utility below fails open: if JavaScript never runs, hydration fails, or motion
            is reduced, the content renders fully visible rather than stuck hidden.
          </p>
          <ul className="mt-6 space-y-4">
            {motionUtilities.map((m) => (
              <li key={m.name} className="border-b border-ink-200 pb-4">
                <p className="font-mono text-body-sm text-ink-900">{m.name}</p>
                <p className="mt-1 text-body-sm text-ink-600">{m.note}</p>
              </li>
            ))}
          </ul>

          <div className="mt-10 space-y-4">
            <p className="text-body-sm font-medium text-ink-900">Reveal — live demo</p>
            {[0, 80, 160].map((d) => (
              <Reveal key={d}>
                <Card className="rounded-2xl border border-ink-200 bg-paper-0 p-6">
                  <p className="text-body text-ink-600">Reveal with {d}ms stagger delay</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <Section tone="dark">
        <Container>
          <Eyebrow>Aurora — live demo</Eyebrow>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <div className="relative h-64 overflow-hidden rounded-2xl border border-paper-0/12 bg-paper-50">
              <Aurora variant="light" />
              <p className="relative p-6 text-body-sm text-ink-900">variant=&quot;light&quot;</p>
            </div>
            <div className="relative h-64 overflow-hidden rounded-2xl border border-paper-0/12">
              <Aurora variant="dark" />
              <p className="relative p-6 text-body-sm text-paper-0">variant=&quot;dark&quot;</p>
            </div>
          </div>
        </Container>
      </Section>
    </main>
  );
}
